import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';

import { createRoot } from 'react-dom/client';
import PropTypes from 'prop-types';
import { Button, InfoBlock } from '@picsio/ui';

import localization from '../../../../shared/strings';
import Checkbox from '../../../../UIComponents/checkbox';
import Tooltip from '../../../Tooltip';
import Icon from '../../../Icon';
import {
  CHANNEL_SYNC_ACTION,
  getDefaultPublishingScope,
  hasAttachedMediaForSync,
  hasListingForChannel,
  isMediaInScope,
  isMediaScopeAvailable,
  MEDIA_SYNC_MODE,
  SYNC_SETUP_MODE,
} from '../helpers/syncFlowOrchestrator';
import { getListingStatusText, hasDraftListingForChannel, hasLiveListingForChannel } from '../helpers/syncFlowHelpers';
import ButtonLoader from '../ButtonLoader';
import MediaModeToggle from '../MediaModeToggle';
import styles from '../styles.module.scss';

const normalizePublishingScope = (scope) => {
  if (!scope) {
    return scope;
  }

  if (scope.media === true) {
    return { ...scope, media: MEDIA_SYNC_MODE.REPLACE };
  }

  return scope;
};

const buildInitialCheckedChannels = (channelOptions, initialSetupState) => {
  if (initialSetupState?.selectedChannels?.length) {
    return channelOptions.reduce((acc, option) => ({
      ...acc,
      [option.value]: !option.disconnected && initialSetupState.selectedChannels.includes(option.value),
    }), {});
  }

  const selectableOptions = channelOptions.filter((option) => !option.disconnected);

  return channelOptions.reduce((acc, option) => ({
    ...acc,
    [option.value]: !option.disconnected && selectableOptions.length === 1,
  }), {});
};

function PublishingScopeSetupDialog({
  mode,
  channelOptions,
  productListings,
  product,
  productsState,
  isVariantSync,
  masterVariantIds,
  shouldShowVariantNote,
  initialSetupState,
  onProceed,
  onCancel,
  onValidationAbort,
  root,
}) {
  const strings = localization.DIALOGS.PRODUCT_DIALOGS.CONNECT_CHANNEL;
  const setupStrings = strings.SETUP_PUBLISHING_SCOPE;
  const scopeStrings = strings.PUBLISHING_SCOPE;
  const bulkStrings = localization.ACTIONS_TOOLBAR.PRODUCTS;
  const lockedAction = mode === SYNC_SETUP_MODE.PUBLISH
    ? CHANNEL_SYNC_ACTION.PUBLISH
    : CHANNEL_SYNC_ACTION.DRAFT;

  const defaultScope = useMemo(
    () => getDefaultPublishingScope({
      product,
      isVariantSync,
      masterVariantIds,
      productsState,
    }),
    [isVariantSync, masterVariantIds, product, productsState],
  );

  const isMediaAvailable = useMemo(
    () => isMediaScopeAvailable({
      product,
      isVariantSync,
      masterVariantIds,
      productsState,
    }),
    [isVariantSync, masterVariantIds, product, productsState],
  );

  const mediaDisabledTooltip = useMemo(() => {
    if (isMediaAvailable) {
      return null;
    }

    if (!hasAttachedMediaForSync({
      product, isVariantSync, masterVariantIds, productsState,
    })) {
      return scopeStrings.MEDIA_NO_ASSETS_TOOLTIP;
    }

    return null;
  }, [
    isMediaAvailable,
    isVariantSync,
    masterVariantIds,
    product,
    productsState,
    scopeStrings.MEDIA_NO_ASSETS_TOOLTIP,
  ]);

  const [checkedChannels, setCheckedChannels] = useState(
    () => buildInitialCheckedChannels(channelOptions, initialSetupState),
  );
  const [publishingScope, setPublishingScope] = useState(
    () => normalizePublishingScope(initialSetupState?.publishingScope) ?? defaultScope,
  );
  const [lastMediaMode, setLastMediaMode] = useState(() => {
    const initialMedia = normalizePublishingScope(
      initialSetupState?.publishingScope,
    )?.media;
    return isMediaInScope(initialMedia) && initialMedia !== true
      ? initialMedia
      : MEDIA_SYNC_MODE.REPLACE;
  });
  const [isProceeding, setIsProceeding] = useState(false);

  const selectedChannelIds = useMemo(
    () => Object.entries(checkedChannels)
      .filter(([, checked]) => checked)
      .map(([channelId]) => channelId),
    [checkedChannels],
  );

  const hasChannelSelection = selectedChannelIds.length > 0;
  const requiresTextForFirstCreate = selectedChannelIds.some(
    (channelId) => !hasListingForChannel(productListings, channelId),
  );
  const mediaInScope = isMediaInScope(publishingScope.media);
  const hasScopeSelection = publishingScope.text || mediaInScope;
  const showVariantNote = isVariantSync && selectedChannelIds.some(
    (channelId) => shouldShowVariantNote?.(channelId),
  );
  const showMediaModeCards = mediaInScope && selectedChannelIds.some((channelId) => (
    lockedAction === CHANNEL_SYNC_ACTION.PUBLISH
      ? hasLiveListingForChannel({
        channelId,
        product,
        productsState,
        productListings,
        masterVariantIds,
      })
      : hasDraftListingForChannel({
        channelId,
        productListings,
      })
  ));
  const resolvedMedia = mediaInScope && !showMediaModeCards
    ? MEDIA_SYNC_MODE.REPLACE
    : publishingScope.media;

  const proceedLabel = mediaInScope
    ? strings.BTN_PROCEED_TO_MEDIA
    : strings.BTN_PROCEED;

  const shouldValidateOnProceed = publishingScope.text && !mediaInScope;
  const title = mode === SYNC_SETUP_MODE.PUBLISH
    ? bulkStrings.publishToChannels
    : bulkStrings.createDraftsSetupTitle;
  const description = mode === SYNC_SETUP_MODE.PUBLISH
    ? bulkStrings.publishToChannelsDescriptionSingle
    : bulkStrings.createDraftsSetupDescriptionSingle;

  const proceedTooltip = useMemo(() => {
    if (!hasChannelSelection && !hasScopeSelection) {
      return strings.SELECT_CHANNEL_AND_SCOPE_TOOLTIP;
    }

    if (!hasChannelSelection) {
      return strings.SELECT_CHANNEL_TOOLTIP;
    }

    if (!hasScopeSelection) {
      return strings.SELECT_SCOPE_TOOLTIP;
    }

    return null;
  }, [
    hasChannelSelection,
    hasScopeSelection,
    strings.SELECT_CHANNEL_AND_SCOPE_TOOLTIP,
    strings.SELECT_CHANNEL_TOOLTIP,
    strings.SELECT_SCOPE_TOOLTIP,
  ]);

  useEffect(() => {
    if (!requiresTextForFirstCreate) {
      return;
    }

    setPublishingScope((prev) => (prev.text ? prev : { ...prev, text: true }));
  }, [requiresTextForFirstCreate]);

  const isDestroyedRef = useRef(false);

  const destroy = useCallback(() => {
    if (isDestroyedRef.current) {
      return;
    }

    isDestroyedRef.current = true;

    try {
      root.unmount();
    } catch { /* ignore */ }
  }, [root]);

  const handleCancel = useCallback(() => {
    destroy();
    onCancel?.();
  }, [destroy, onCancel]);

  const handleProceed = useCallback(async () => {
    const selectedChannelActions = selectedChannelIds.reduce((acc, channelId) => ({
      ...acc,
      [channelId]: lockedAction,
    }), {});

    const payload = {
      selectedChannels: selectedChannelIds,
      channelActionsByChannelId: selectedChannelActions,
      publishingScope: {
        ...publishingScope,
        media: resolvedMedia,
      },
      overrideExisting: true,
    };

    if (shouldValidateOnProceed) {
      setIsProceeding(true);

      try {
        await onProceed(payload, {
          onUnmappedDialogOpen: () => {
            setIsProceeding(false);
            destroy();
          },
        });
        destroy();
      } catch {
        if (isDestroyedRef.current) {
          onValidationAbort?.();
          return;
        }

        setIsProceeding(false);
      }

      return;
    }

    destroy();
    onProceed(payload);
  }, [
    destroy,
    lockedAction,
    onProceed,
    publishingScope,
    resolvedMedia,
    selectedChannelIds,
    shouldValidateOnProceed,
    onValidationAbort,
  ]);

  const handleChannelToggle = useCallback((channelId) => {
    const option = channelOptions.find(({ value }) => value === channelId);
    if (option?.disconnected) {
      return;
    }

    setCheckedChannels((prev) => ({ ...prev, [channelId]: !prev[channelId] }));
  }, [channelOptions]);

  const handleTextToggle = useCallback(() => {
    if (requiresTextForFirstCreate) {
      return;
    }
    setPublishingScope((prev) => ({ ...prev, text: !prev.text }));
  }, [requiresTextForFirstCreate]);

  const handleMediaToggle = useCallback(() => {
    if (!isMediaAvailable) return;
    setPublishingScope((prev) => {
      if (isMediaInScope(prev.media)) {
        return { ...prev, media: false };
      }
      return { ...prev, media: lastMediaMode };
    });
  }, [isMediaAvailable, lastMediaMode]);

  const handleMediaModeChange = useCallback((mediaMode) => {
    if (!isMediaInScope(mediaMode)) return;
    setLastMediaMode(mediaMode);
    setPublishingScope((prev) => ({ ...prev, media: mediaMode }));
  }, []);

  const getChannelReplaceWarningText = useCallback((channelId, isSelected) => {
    if (!isSelected) {
      return null;
    }

    if (lockedAction === CHANNEL_SYNC_ACTION.DRAFT && hasDraftListingForChannel({
      channelId,
      productListings,
    })) {
      return setupStrings.EXISTING_DRAFT_WILL_BE_REPLACED;
    }

    return null;
  }, [lockedAction, productListings, setupStrings]);

  const getChannelStatusText = useCallback((channelId, isDisconnected) => {
    if (isDisconnected) {
      return setupStrings.CONNECTION_LOST;
    }

    const statusText = getListingStatusText(productListings?.[channelId]);

    if (statusText === 'Not set up yet') {
      return setupStrings.NOT_PUBLISHED_YET;
    }

    return statusText;
  }, [productListings, setupStrings.CONNECTION_LOST, setupStrings.NOT_PUBLISHED_YET]);

  return (
    <div className={`simpleDialog ${styles.connectChannelSectionModal} ${styles.connectChannelSectionDialogWithSubheader}`}>
      <div className="simpleDialogUnderlayer" />
      <div className={`simpleDialogBox ${styles.connectChannelSectionModalBox}`}>
        <div className="simpleDialogHeader">
          <span className="simpleDialogTitle">{title}</span>
          <span
            role="button"
            tabIndex={0}
            className="simpleDialogBtnCross"
            aria-disabled={isProceeding}
            onClick={handleCancel}
            onKeyDown={handleCancel}
          >
            <Icon name="close" />
          </span>
        </div>
        <div className="simpleDialogSubHeader">
          <p>{description}</p>
        </div>
        <div className="simpleDialogContent">
          <div className="simpleDialogContentInner">
            <If condition={showVariantNote}>
              <InfoBlock className="m-b-12">{strings.VARIANT_NOTE}</InfoBlock>
            </If>

            <div className={styles.connectChannelSectionSetupSection}>
              <div className={styles.connectChannelSectionSetupSectionTitle}>
                {setupStrings.TARGET_CHANNELS_TITLE}
              </div>
              <div className={styles.connectChannelSectionSetupSectionDivider} />
              {channelOptions.map((option, index) => {
                const isDisconnected = Boolean(option.disconnected);
                const isSelected = checkedChannels[option.value];
                const replaceWarningText = getChannelReplaceWarningText(
                  option.value,
                  isSelected,
                );

                const channelRow = (
                  <div className={styles.connectChannelSectionChannelRow}>
                    <div className={styles.connectChannelSectionChannelRowMain}>
                      <Checkbox
                        className="w-100"
                        value={checkedChannels[option.value]}
                        disabled={isDisconnected || isProceeding}
                        label={(
                          <span className={styles.connectChannelSectionChannelRowLabel}>
                            <span className={styles.connectChannelSectionChannelRowTitle}>
                              <If condition={option.icon}>
                                <span className={styles.connectChannelSectionChannelRowIcon}>
                                  {option.icon}
                                </span>
                              </If>
                              <span className={styles.connectChannelSectionChannelRowName}>
                                {option.label}
                              </span>
                            </span>
                            <If condition={replaceWarningText}>
                              <span className={styles.connectChannelSectionChannelRowDraftWarning}>
                                {replaceWarningText}
                              </span>
                            </If>
                            <span className={styles.connectChannelSectionChannelRowStatus}>
                              ({getChannelStatusText(option.value, isDisconnected)})
                            </span>
                          </span>
                        )}
                        onChange={() => handleChannelToggle(option.value)}
                      />
                    </div>
                  </div>
                );

                const rowElement = isDisconnected ? (
                  <Tooltip content={setupStrings.CONNECTION_LOST_TOOLTIP}>
                    {channelRow}
                  </Tooltip>
                ) : channelRow;

                return (
                  <React.Fragment key={option.value}>
                    {rowElement}
                    <If condition={index < channelOptions.length - 1}>
                      <div
                        className={styles.connectChannelSectionChannelRowDivider}
                        data-testid="channel-row-divider"
                      />
                    </If>
                  </React.Fragment>
                );
              })}
            </div>

            <div className={styles.connectChannelSectionSetupSection}>
              <div className={styles.connectChannelSectionSetupSectionTitle}>
                {setupStrings.DATA_TO_SYNCHRONIZE_TITLE}
              </div>
              <div className={styles.connectChannelSectionSetupSectionDivider} />
              <Tooltip content={requiresTextForFirstCreate
                ? scopeStrings.TEXT_REQUIRED_FOR_FIRST_PUBLISH
                : null}
              >
                <div className={styles.connectChannelSectionScopeOption}>
                  <Checkbox
                    value={publishingScope.text}
                    disabled={requiresTextForFirstCreate}
                    label={(
                      <span className={styles.connectChannelSectionScopeLabel}>
                        <span className={styles.connectChannelSectionScopeTitle}>
                          {scopeStrings.TEXT_ATTRIBUTES_TITLE}
                        </span>
                        <span className={styles.connectChannelSectionScopeDescription}>
                          {scopeStrings.TEXT_ATTRIBUTES_DESCRIPTION}
                        </span>
                      </span>
                    )}
                    onChange={handleTextToggle}
                  />
                </div>
              </Tooltip>
              <Tooltip content={mediaDisabledTooltip}>
                <div className={styles.connectChannelSectionScopeOption}>
                  <Checkbox
                    value={mediaInScope}
                    disabled={!isMediaAvailable}
                    label={(
                      <span className={styles.connectChannelSectionScopeLabel}>
                        <span className={styles.connectChannelSectionScopeTitle}>
                          {scopeStrings.MEDIA_ATTRIBUTES_TITLE}
                        </span>
                        <span className={styles.connectChannelSectionScopeDescription}>
                          {showMediaModeCards
                            ? scopeStrings.MEDIA_ATTRIBUTES_APPLY_DESCRIPTION
                            : scopeStrings.MEDIA_ATTRIBUTES_DESCRIPTION}
                        </span>
                      </span>
                    )}
                    onChange={handleMediaToggle}
                  />
                  <If condition={showMediaModeCards}>
                    <MediaModeToggle
                      mediaMode={
                        publishingScope.media === MEDIA_SYNC_MODE.ADD
                          ? MEDIA_SYNC_MODE.ADD
                          : MEDIA_SYNC_MODE.REPLACE
                      }
                      disabled={isProceeding}
                      onChange={handleMediaModeChange}
                    />
                  </If>
                </div>
              </Tooltip>
            </div>
          </div>
        </div>
        <div className="simpleDialogFooter">
          <Button
            color="secondary"
            size="md"
            variant="contained"
            disabled={isProceeding}
            onClick={handleCancel}
          >
            {strings.BTN_CANCEL}
          </Button>
          <Tooltip content={proceedTooltip}>
            <Button
              color="primary"
              size="md"
              variant="contained"
              disabled={!hasChannelSelection || !hasScopeSelection || isProceeding}
              endIcon={isProceeding ? <ButtonLoader /> : null}
              onClick={handleProceed}
            >
              {proceedLabel}
            </Button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
}

PublishingScopeSetupDialog.propTypes = {
  mode: PropTypes.oneOf([SYNC_SETUP_MODE.PUBLISH, SYNC_SETUP_MODE.CREATE_DRAFTS]).isRequired,
  channelOptions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    type: PropTypes.string,
    icon: PropTypes.node,
    disconnected: PropTypes.bool,
  })).isRequired,
  productListings: PropTypes.objectOf(PropTypes.shape({})),
  product: PropTypes.shape({}),
  productsState: PropTypes.shape({}),
  isVariantSync: PropTypes.bool,
  masterVariantIds: PropTypes.arrayOf(PropTypes.string),
  shouldShowVariantNote: PropTypes.func,
  initialSetupState: PropTypes.shape({
    selectedChannels: PropTypes.arrayOf(PropTypes.string),
    channelActionsByChannelId: PropTypes.objectOf(PropTypes.string),
    publishingScope: PropTypes.shape({
      text: PropTypes.bool,
      media: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    }),
  }),
  root: PropTypes.shape({ unmount: PropTypes.func.isRequired }).isRequired,
  onProceed: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  onValidationAbort: PropTypes.func,
};

PublishingScopeSetupDialog.defaultProps = {
  productListings: undefined,
  product: undefined,
  productsState: undefined,
  isVariantSync: false,
  masterVariantIds: undefined,
  shouldShowVariantNote: () => false,
  initialSetupState: undefined,
  onCancel: undefined,
  onValidationAbort: undefined,
};

export function showPublishingScopeSetupDialog({
  mode,
  channelOptions,
  productListings,
  product,
  productsState,
  isVariantSync,
  masterVariantIds,
  shouldShowVariantNote,
  initialSetupState,
  onProceed,
  onCancel,
  onValidationAbort,
}) {
  const $wrapper = document.querySelector('.wrapperDialog');
  const root = createRoot($wrapper);
  root.render(
    <PublishingScopeSetupDialog
      mode={mode}
      channelOptions={channelOptions}
      productListings={productListings}
      product={product}
      productsState={productsState}
      isVariantSync={isVariantSync}
      masterVariantIds={masterVariantIds}
      shouldShowVariantNote={shouldShowVariantNote}
      initialSetupState={initialSetupState}
      root={root}
      onProceed={onProceed}
      onCancel={onCancel}
      onValidationAbort={onValidationAbort}
    />,
  );
}

export default PublishingScopeSetupDialog;
